import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Summary } from '../../../../types';
import { SummarizerConfig } from '../../../../types/summarizer';
import { Button, ErrorAlert, SummaryOptionsBar, Languages, ExportDropdown } from '@components/ui';
import { Markdown } from '@components/ui/markdown';
import { RefreshCw, Sparkles, Globe } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import { useProcessingSteps } from '@core/hooks/useProcessingSteps';
import { CopyButton } from '@components/ui/CopyButton';
import { useSummarizer } from '@core/store/useSummarizer';
import { getApiUrl } from '../../../../lib/hooks/use-new-api';
import { getCTADialogType, getUserFriendlyErrorMessage } from '../../../../lib/error-utils';
import {
  copyToClipboard,
  exportAsTextFile,
  exportAsPDF,
  generateFilename,
} from '../../../../lib/content-utils';
import { useToast, toast } from '@components/ui/toast';
import { useMediaQuery } from 'usehooks-ts';
import { useCompletion } from '../../../../lib/use-completion';
import { useTranslation } from '../../../../lib/hooks/useTranslation';

interface SummaryTabProps {
  summary: Summary;
  config?: SummarizerConfig;
}

export const SummaryTab: React.FC<SummaryTabProps> = ({ summary, config }) => {
  const {
    setSummary,
    summaryType,
    setSummaryType,
    tone,
    setTone,
    translations,
    quota,
    customPrompt,
    currentConfig,
    triggerCTADialog,
    getCachedSummary,
    setCachedSummary,
  } = useSummarizer();
  const summaryId = summary.id ?? null;
  const [retryCount, setRetryCount] = useState(0);
  const { addToast } = useToast();
  const [language, setLanguage] = useState('en-US');
  const isMobile = useMediaQuery('(max-width: 768px)');

  // Use translation hook
  const { translateSummary, getTranslatedContent, isTranslating, loadingLanguage } =
    useTranslation();

  // Determine which API to use
  const apiUrl = getApiUrl('/generate-summary');

  // Determine the prompt to use (priority: user input > config > default)
  const effectivePrompt = customPrompt || currentConfig?.prompt || '';

  // Use useStreamText for direct text streaming with proper payload
  const {
    completion: content,
    loading,
    setCompletion: setContent,
    complete: stream,
    error,
    isGenerating,
  } = useCompletion({
    api: apiUrl,
    body: {
      summary_id: summaryId,
      transcript: summary.transcript || '',
      detail_level: summaryType.toLowerCase() || 'Detailed',
      tone_name: tone.toLowerCase() || 'neutral',
      language: language || 'en-US', // Include language in the request
      user_id: quota?.user_id,
      ...(effectivePrompt && { prompt: effectivePrompt }),
    },
    onFinish: (_, content, payloads = {}) => {
      if (content) {
        const newSummary = {
          ...summary,
          summary: content,
          summary_as_md: content,
        };
        setSummary(newSummary);
        setRetryCount(0);
        // Cache the generated summary with language
        setCachedSummary(
          content,
          payloads.detail_level || summaryType,
          payloads.tone_name || tone,
          payloads.language || language
        );
      }
    },
    onError: (err) => {
      // Check if we should show CTA modal
      if (err.shouldShowCTA) {
        const ctaType = getCTADialogType(err);
        if (ctaType) {
          triggerCTADialog(ctaType, 'summary', err.message);
        }
      }
    },
  });

  // Helper function to handle smart summary generation with caching
  const generateSummaryWithCache = useCallback(
    (newSummaryType = summaryType, newTone = tone, newLanguage = language) => {
      const cachedContent = getCachedSummary(newSummaryType, newTone, newLanguage);
      if (cachedContent) {
        // Use cached content immediately
        setContent(cachedContent);
        const newSummary = {
          ...summary,
          summary: cachedContent,
          summary_as_md: cachedContent,
        };
        setSummary(newSummary);
        return;
      }

      setContent('');
      // No cache found, stream new content
      stream(effectivePrompt, {
        summary_id: summaryId,
        detail_level: newSummaryType.toLowerCase() || 'Detailed',
        tone_name: newTone.toLowerCase() || 'Neutral',
        language: newLanguage || 'en-US',
      });
    },
    [
      summaryType,
      tone,
      language,
      effectivePrompt,
      getCachedSummary,
      setContent,
      setSummary,
      summary,
      stream,
      summaryId,
      setCachedSummary,
    ]
  );

  const handleRetry = () => {
    setRetryCount((prev) => prev + 1);
    stream(effectivePrompt, {
      summary_id: summaryId,
      detail_level: summaryType.toLowerCase() || 'Detailed',
      tone_name: tone.toLowerCase() || 'Neutral',
      language: language || 'en-US',
    });
  };

  const handleCopySummaryContent = async () => {
    try {
      const textToCopy =
        displayContent || content || summary.summary_as_md || summary.summary || '';
      if (!textToCopy) {
        return;
      }

      const success = await copyToClipboard(textToCopy);

      if (!success) {
        addToast(toast.error('Failed to copy summary'));
      }
    } catch (error) {
      addToast(toast.error('Failed to copy summary'));
    }
  };

  const handleExportSummaryContent = async () => {
    try {
      const textToExport =
        displayContent || content || summary.summary_as_md || summary.summary || '';

      if (!textToExport) {
        return;
      }

      const filename = generateFilename(summary.title ? `${summary.title}_summary` : 'summary');

      exportAsTextFile(textToExport, filename);
      addToast(toast.success('Summary exported as TXT successfully'));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      addToast(toast.error(`Failed to export summary: ${errorMessage}`));
    }
  };

  const handleExportSummaryAsPDF = async () => {
    try {
      const textToExport =
        displayContent || content || summary.summary_as_md || summary.summary || '';

      if (!textToExport) {
        return;
      }

      const title = summary.title ? `${summary.title} - Summary` : 'Summary';
      const filename = generateFilename(
        summary.title ? `${summary.title}_summary` : 'summary',
        'pdf'
      );

      await exportAsPDF(textToExport, title, filename);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      addToast(toast.error(`Failed to export PDF: ${errorMessage}`));
    }
  };

  useEffect(() => {
    // Reset local state when a new summary arrives
    setLanguage('en-US');
    setRetryCount(0);

    if (!quota?.user_id) {
      return;
    }
    if (summary.summary_as_md) {
      setContent(summary.summary_as_md);
    } else if (summaryId && summary.transcript) {
      setContent('');
      stream(effectivePrompt, {
        summary_id: summaryId,
        detail_level: summaryType?.toLowerCase() || 'detailed',
        tone_name: tone?.toLowerCase() || 'neutral',
        language: 'en-US',
      });
    }
  }, [summary.id, quota?.user_id, effectivePrompt]);

  const onLanguageChange = (newLanguage: string) => {
    setLanguage(newLanguage);

    // For English, just regenerate with current settings
    if (newLanguage === 'en' || newLanguage === 'en-US') {
      generateSummaryWithCache(summaryType, tone, 'en-US');
      return;
    }

    // For other languages, check cache first, then translate if needed
    const cachedContent = getCachedSummary(summaryType, tone, newLanguage);
    if (cachedContent) {
      setContent(cachedContent);
      return;
    }

    // If no cache, translate current content
    translateSummary(newLanguage);
  };

  // Get the display content based on current state
  const displayContent = useMemo(() => {
    // If we're loading a translation, show loading state
    if (isTranslating && loadingLanguage === language) {
      return null; // Will show loading state instead
    }

    // For English, always use the generated content
    if (language === 'en' || language === 'en-US') {
      return content;
    }

    // For other languages, check for translated content with matching detail_level and tone
    const translatedContent = getTranslatedContent(language, summaryType, tone);

    if (translatedContent?.summary_as_md) {
      return translatedContent.summary_as_md;
    }

    // If no translation exists but we have cached content for this combination
    const cachedContent = getCachedSummary(summaryType, tone, language);
    if (cachedContent) {
      return cachedContent;
    }

    // Fall back to English content if no translation available
    return content;
  }, [
    language,
    translations,
    content,
    getTranslatedContent,
    isTranslating,
    loadingLanguage,
    summaryType,
    tone,
    getCachedSummary,
  ]);

  return (
    <div>
      <div className="p-4 sticky top-0 w-full bg-white z-10">
        <div className="flex gap-2 items-center justify-between">
          <div className="flex gap-2 items-center">
            {config?.inputs?.showSummaryOptions !== false && (
              <SummaryOptionsBar
                summaryType={summaryType}
                tone={tone}
                onSummaryTypeChange={(type) => {
                  setSummaryType(type);
                  generateSummaryWithCache(type, tone, language);
                }}
                onToneChange={(newTone) => {
                  setTone(newTone);
                  generateSummaryWithCache(summaryType, newTone, language);
                }}
                disabled={loading || !displayContent}
                className="flex-1"
                gap="md"
                showSummaryType={config?.inputs?.showSummaryType !== false}
                showTone={config?.inputs?.showTone !== false}
              />
            )}

            {currentConfig?.outputs?.translation && (
              <Languages
                isLoading={isTranslating}
                value={language}
                onChange={onLanguageChange}
                icon={isMobile}
                disabled={!summary.summary}
              />
            )}
          </div>

          <div className="flex items-center gap-2">
            {(displayContent || content) && (
              <>
                <CopyButton onCopy={handleCopySummaryContent} showText={!isMobile} />
                {currentConfig?.outputs?.download && (
                  <ExportDropdown
                    onExportTxt={handleExportSummaryContent}
                    onExportPdf={handleExportSummaryAsPDF}
                    disabled={loading || !displayContent}
                    loading={loading}
                    showText={!isMobile}
                    variant="outline"
                    size="sm"
                  />
                )}
              </>
            )}
            {error && (
              <Button
                onClick={handleRetry}
                variant="outline"
                size="sm"
                disabled={loading}
                className="flex items-center gap-2"
              >
                <RefreshCw className={cn('w-4 h-4', loading && 'animate-spin')} />
                Retry {retryCount > 0 && `(${retryCount})`}
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="px-4 pb-4">
        {/* Error State */}
        {error && !loading && (
          <ErrorAlert
            message={getUserFriendlyErrorMessage(error)}
            details={error.details ? JSON.stringify(error.details, null, 2) : undefined}
            type={error.code === 'QUOTA_EXCEEDED' ? 'warning' : 'error'}
            title={
              error.code === 'QUOTA_EXCEEDED' ? 'Quota Exceeded' : 'Failed to generate summary'
            }
            className="mb-4"
          />
        )}

        {/* Loading State */}
        {loading && !content && (
          <AiLoadingCard
            icon={<Sparkles className="w-6 h-6 text-blue-500" />}
            title={config?.ui?.loadingMessages?.aiGeneration?.title || 'AI is generating your summary...'}
            steps={[
              'Reading and understanding your content...',
              'AI is extracting key themes and insights...',
              'Organizing information into a clear structure...',
              'Generating your personalized summary...',
              'Polishing and formatting results...',
            ]}
            isActive={loading && !content}
            variant="blue"
          />
        )}

        {/* Translation Loading State */}
        {isTranslating && loadingLanguage === language && !loading && (
          <AiLoadingCard
            icon={<Globe className="w-6 h-6 text-purple-500" />}
            title={(config?.ui?.loadingMessages?.translation?.title || 'Translating content to {language}...').replace('{language}', language)}
            steps={[
              'Preparing translation engine...',
              `Converting content to ${language}...`,
              'Ensuring accuracy and natural flow...',
            ]}
            isActive={isTranslating && loadingLanguage === language && !loading}
            variant="purple"
          />
        )}

        {displayContent && !isTranslating && (
          <div className="prose prose-sm max-w-none">
            <Markdown>{displayContent || ''}</Markdown>
            {isGenerating && (
              <div className="pulse-animation mt-2 h-2 w-2 rounded-full bg-brand-primary"></div>
            )}
          </div>
        )}

        {/* Empty state when no content and no loading */}
        {!displayContent && !loading && !error && !isTranslating && !isGenerating && (
          <div className="text-center py-8 text-gray-500">
            <Sparkles className="w-8 h-8 mx-auto mb-3 text-gray-400" />
            <p>No summary content available.</p>
          </div>
        )}
      </div>
    </div>
  );
};

/* ── Premium AI Loading Card (inline) ── */

interface AiLoadingCardProps {
  icon: React.ReactNode;
  title: string;
  steps: string[];
  isActive: boolean;
  variant: 'blue' | 'purple';
}

const VARIANT_STYLES = {
  blue: {
    card: 'ai-loading-card ai-loading-card--blue',
    title: 'text-blue-800',
    step: 'text-blue-600',
    orb: 'ai-orb-container ai-orb-container--blue',
    bar: 'ai-progress-bar ai-progress-bar--blue',
  },
  purple: {
    card: 'ai-loading-card ai-loading-card--purple',
    title: 'text-purple-800',
    step: 'text-purple-600',
    orb: 'ai-orb-container ai-orb-container--purple',
    bar: 'ai-progress-bar ai-progress-bar--purple',
  },
};

function AiLoadingCard({ icon, title, steps, isActive, variant }: AiLoadingCardProps) {
  const { currentStep } = useProcessingSteps({
    isActive,
    steps,
    intervalMs: 3000,
  });

  const s = VARIANT_STYLES[variant];

  return (
    <div className={cn('mb-4', s.card)}>
      <div className="flex items-start gap-4 p-5">
        {/* Orbiting dots visualization */}
        <div className={s.orb}>
          <div className="ai-orb-center">{icon}</div>
          <span className="ai-orb-dot ai-orb-dot--1" />
          <span className="ai-orb-dot ai-orb-dot--2" />
          <span className="ai-orb-dot ai-orb-dot--3" />
        </div>

        {/* Text content */}
        <div className="flex-1 min-w-0 pt-1">
          <p className={cn('text-sm font-semibold mb-1', s.title)}>{title}</p>
          <p key={currentStep} className={cn('text-sm ai-step-text', s.step)}>
            {currentStep}
          </p>

          {/* Progress bar */}
          <div className={cn('mt-3', s.bar)}>
            <div className="ai-progress-bar__fill" />
          </div>
        </div>
      </div>
    </div>
  );
}
